import {
  ampRxnTrigElmts,
  baseRxnDmg,
  NAs,
  physAndElmts,
  rdAndIg,
  tfmRxns
} from "../configs";
import { checkCharMC } from "../data/characters/helpers";
import tlLvMults from "../data/characters/tlLvMults";
import { findArtifact, findCharacter } from "../data/controllers";
import {
  addOrInit,
  bareLv,
  findByIndex,
  findByName,
  getFinalTlLv,
  toMult
} from "../helpers";
import { lib } from "../library";
import {
  addAndTrack,
  calcDmg,
  dmgFormula,
  getDefMult,
  getResMult,
  pickTlBn,
  pushOrMerge
} from "./helpers";

function addCustomDebuffs(rdMult, customDCs, tracker) {
  for (const { type, val } of customDCs) {
    const desc = "Custom Debuff";
    if (type === "DEF reduction") {
      rdMult.Def_rd += val;
      pushOrMerge(tracker, "Def_rd", desc, val);
    } else {
      // type = ${elmt} RES reduction
      const field = `${type.slice(0, -14)}_rd`;
      rdMult[field] += val;
      pushOrMerge(tracker, field, desc, val);
    }
  }
}

function addSelfDebuffs(wrapper, charDCs, debuffs, char) {
  for (const { activated, inputs, index } of charDCs) {
    const debuff = findByIndex(debuffs, index);
    if (debuff && debuff.isGranted(char) && activated) {
      const tkDesc = `Self / ${debuff.src}`;
      debuff.addPntes({ ...wrapper, fromSelf: true, char, inputs, tkDesc });
    }
  }
}

function addPartyDebuffs(wrapper, party) {
  for (const tm of party.filter((m) => m)) {
    const { debuffs } = findCharacter(tm, "debuffs");
    for (const { activated, inputs, index } of tm.DCs) {
      const debuff = findByIndex(debuffs, index);
      if (!activated || !debuff) continue;
      const tkDesc = `${tm.name} / ${debuff.src}`;
      debuff.addPntes({ ...wrapper, fromSelf: false, inputs, tkDesc });
    }
  }
}

function addArtDebuffs(wrapper, artDCs) {
  for (const { activated, code, index, inputs } of artDCs) {
    if (activated) {
      const { name, debuffs } = findArtifact({ code });
      const tkDesc = `${name} / 4-Piece activated`;
      debuffs[index].addPntes({ ...wrapper, inputs, tkDesc });
    }
  }
}

function addResonanceDebuffs(rdMult, elmtMCs, tracker) {
  const geoRsn = findByName(elmtMCs.resonance, "Enduring Rock");
  if (geoRsn && geoRsn.activated) {
    addAndTrack("Geo Resonance", rdMult, "Geo_rd", 20, tracker);
  }
  if (elmtMCs.superconduct) {
    addAndTrack("Superconduct", rdMult, "Physical_rd", 40, tracker);
  }
}

function getBaseDmg(ATTRs, dmgPiece, level, tlBnes) {
  const { baseMult, multType, baseFlat, flatType } = dmgPiece;
  const statT = dmgPiece.baseSType || "ATK";
  const xtraMult = pickTlBn(tlBnes, "mult");
  const flatBn = baseFlat ? baseFlat * tlLvMults[flatType][level] : 0;
  const values = [ATTRs[statT], statT];

  const totalPct = (thisMult) =>
    thisMult * tlLvMults[multType][level] + xtraMult;
  const baseDmg = (thisPct) => {
    let result = (ATTRs[statT] * thisPct) / 100;
    values[3] = flatBn;
    return result + flatBn;
  };
  if (Array.isArray(baseMult)) {
    const pcts = baseMult.map(totalPct);
    values[2] = pcts;
    return [pcts.map(baseDmg), values];
  }
  const pct = totalPct(baseMult);
  values[2] = pct;
  return [baseDmg(pct), values];
}

function getTotalFlat(tlBnes, hitBnes, [attPatt, attElmt]) {
  return (
    pickTlBn(tlBnes, "flat") +
    (hitBnes[attPatt]?.flat || 0) +
    (hitBnes[attElmt]?.flat || 0)
  );
}

function getDmgBnMult(tlBnes, [attPatt, attElmt], hitBnes, inf, ATTRs) {
  let normal = pickTlBn(tlBnes, "pct") + (hitBnes[attPatt]?.pct || 0);
  if (NAs.includes(attPatt) && attElmt === "Physical" && inf !== "Physical") {
    normal += ATTRs[inf + " DMG Bonus"];
  } else {
    normal += hitBnes[attElmt]?.pct || 0;
  }
  let special = 1; // attPatt can be null
  if (attPatt && hitBnes[attPatt]?.sMult) {
    special += hitBnes[attPatt].sMult / 100;
  }
  return [toMult(hitBnes.All.pct + normal), special];
}

function getRxnMult(elmtMCs, attElmt, inf, rxnBnes, vision) {
  const { ampRxn, naAmpRxn } = elmtMCs;
  if (
    ampRxn &&
    (attElmt === "Elemental" ||
      (inf === vision && ampRxnTrigElmts[ampRxn].includes(inf)))
  ) {
    return rxnBnes[ampRxn];
  } else if (
    naAmpRxn &&
    inf !== vision &&
    ampRxnTrigElmts[naAmpRxn].includes(inf)
  ) {
    return rxnBnes["na" + naAmpRxn];
  }
  return 1;
}

function getReduction(char, target, rdMult, [attPatt, attElmt], vision, inf) {
  const defMult = getDefMult(char.level, target.Level, rdMult, attPatt);
  if (attElmt === "Elemental") {
    return [defMult, rdMult[`${vision}_rd`]];
  } else if (attElmt === "Physical" && !NAs.includes(attPatt)) {
    return [defMult, rdMult.Physical_rd];
  } else if (attElmt === "Various") {
    return [defMult, 1];
  }
  return [defMult, rdMult[`${inf}_rd`]];
}

function getCrit(ATTRs, tlBnes, [attPatt, attElmt], hitBnes) {
  const total = (type) =>
    ATTRs[lib[type]] +
    pickTlBn(tlBnes, type) +
    (hitBnes[attPatt]?.[type] || 0) +
    (hitBnes[attElmt]?.[type] || 0);
  return {
    Rate: Math.min(Math.max(hitBnes.All.cRate + total("cRate"), 0), 100) / 100,
    Dmg: total("cDmg") / 100
  };
}

export default function getDmg(
  char,
  charData,
  selfMCs,
  party,
  partyData,
  artDCs,
  ATTRs,
  hitBnes,
  rxnBnes,
  customDCs,
  infusion,
  elmtMCs,
  target,
  tracker
) {
  const rdMult = {};
  for (const key of rdAndIg) {
    rdMult[key] = 0;
  }
  const { actvTalents, vision, debuffs } = findCharacter(char);

  addCustomDebuffs(rdMult, customDCs, tracker);
  addSelfDebuffs({ rdMult, partyData, tracker }, selfMCs.DCs, debuffs, char);
  addPartyDebuffs({ rdMult, tracker }, party);
  addArtDebuffs({ rdMult, tracker }, artDCs);
  addResonanceDebuffs(rdMult, elmtMCs, tracker);
  for (const key of physAndElmts) {
    rdMult[`${key}_rd`] = getResMult(target, rdMult, key);
  }

  const finalResult = {};
  actvTalents.forEach((talent) => {
    const { type } = talent;
    const level = getFinalTlLv(char, talent, partyData);
    finalResult[type] = {};
    if (tracker) tracker[type] = {};

    for (const piece of talent.stats) {
      if (piece.noCalc) continue;
      const { dmgTypes, getTlBnes } = piece;
      let tlBnes;
      if (getTlBnes) {
        tlBnes = getTlBnes({ char, charData, selfMCs, ATTRs, partyData });
      }
      tlBnes = tlBnes || {};
      let [base, values] = getBaseDmg(ATTRs, piece, level, tlBnes);

      if (base && dmgTypes) {
        const inf = infusion[dmgTypes[0]];
        const flat = getTotalFlat(tlBnes, hitBnes, dmgTypes);
        addOrInit(values, 3, flat);
        const [normal, special] = getDmgBnMult(
          tlBnes,
          dmgTypes,
          hitBnes,
          inf,
          ATTRs
        );
        const rxn = getRxnMult(elmtMCs, dmgTypes[1], inf, rxnBnes, vision);
        const [def, res] = getReduction(
          char,
          target,
          rdMult,
          dmgTypes,
          vision,
          inf
        );
        base = calcDmg(base, dmgFormula(flat, normal, special, rxn, def, res));
        const c = getCrit(ATTRs, tlBnes, dmgTypes, hitBnes);
        finalResult[type][piece.name] = {
          "Non-crit": base,
          Crit: calcDmg(base, (n) => n * (1 + c.Dmg)),
          Average: calcDmg(base, (n) => n * (1 + c.Rate * c.Dmg))
        };
        values.push(normal, special, rxn, def, res, c.Rate, c.Dmg);
      } else {
        let flat = 0;
        let normal = 1;
        if (piece.isHealing) {
          flat = pickTlBn(tlBnes, "flat");
          normal += ATTRs["Healing Bonus"] / 100;
        }
        base += flat;
        values[3] += flat;
        if (normal !== 1) {
          base *= normal;
          values.push(normal);
        }
        if (tlBnes.pct) {
          const pct = 1 + pickTlBn(tlBnes, "pct") / 100;
          base *= pct;
          values.push(pct);
        }
        if (piece.getLimit) {
          const limit = piece.getLimit({ ATTRs });
          if (base > limit) {
            base = limit;
            values[11] = ` (limited to ${limit})`;
          }
        }
        finalResult[type][piece.name] = {
          "Non-crit": base,
          Crit: 0,
          Average: base
        };
      }
      if (tracker) {
        tracker[type][piece.name] = { values, tlBnes };
      }
    }
  });

  finalResult["Reactions DMG"] = {};
  if (tracker) tracker.Reactions = {};

  let baseDmg = baseRxnDmg[bareLv(char.level)];
  let nilouA4BuffValue = 0;
  let nahidaC2BuffIsOn = false;

  switch (charData.name) {
    case "Nilou": {
      const { buffs } = findCharacter(char) || {};
      if (checkCharMC(buffs, char, selfMCs.BCs, 1) && ATTRs.HP > 30000) {
        nilouA4BuffValue = Math.min(9 * (ATTRs.HP / 1000 - 30), 400);
      }
      break;
    }
    case "Nahida": {
      const { buffs } = findCharacter(char) || {};
      if (checkCharMC(buffs, char, selfMCs.BCs, 3)) {
        nahidaC2BuffIsOn = true;
      }
      break;
    }
    default:
      break;
  }

  const teammateMap = {};
  for (const teammate of party) {
    if (teammate && teammate.name) {
      teammateMap[teammate.name] = teammate;
    }
  }

  const { Nilou, Nahida } = teammateMap;

  if (Nilou) {
    const { activated, inputs = [] } = findByIndex(Nilou.BCs, 1) || {};
    const maxHP = inputs[0] || 0;

    if (activated && maxHP > 30000) {
      nilouA4BuffValue = Math.min(9 * (maxHP / 1000 - 30), 400);
    }
  }
  if (Nahida) {
    nahidaC2BuffIsOn = findByIndex(Nahida.BCs, 3)?.activated;
  }

  for (const rxn of Object.keys(tfmRxns)) {
    const { mult: normalMult, dmgType } = tfmRxns[rxn];

    const specialPercent =
      rxnBnes[rxn] + (rxn === "Bloom" ? nilouA4BuffValue : 0);
    const specialMult = 1 + specialPercent / 100;
    const resMult = dmgType !== "Various" ? rdMult[`${dmgType}_rd`] : 1;
    const base = baseDmg * normalMult * specialMult * resMult;

    finalResult["Reactions DMG"][rxn] = {
      "Non-crit": base,
      Crit: 0,
      Average: base
    };

    if (tracker) {
      tracker.Reactions[rxn] = [normalMult, specialMult, resMult];
    }
  }

  if (nahidaC2BuffIsOn) {
    for (const rxn of ["Burning", "Bloom", "Hyperbloom", "Burgeon"]) {
      const { [rxn]: rxnDmg } = finalResult["Reactions DMG"];
      rxnDmg.Crit = rxnDmg["Non-crit"] * 2;
      rxnDmg.Average = rxnDmg["Non-crit"] * 1.2;

      if (tracker) {
        tracker.Reactions[rxn][9] = 0.2;
        tracker.Reactions[rxn][10] = 1;
      }
    }
  }

  return finalResult;
}
